import React, {useRef, useState} from 'react';
import {Col, Container, Row, Tab, Tabs} from "react-bootstrap";
import css from "../../css/SettingsPage.module.css";
import SettingsComponent from "./SettingsComponent";
import LoadingSpinner from "../../../components/Loading/LoadingSpinner";
import {useTranslation} from "react-i18next";
import ConfirmModal from "../../../components/Modals/ConfirmModal";
import {SETTINGS_APP, SETTINGS_GENERAL} from "../../../Constants";
import ManageSettingModal from "../../../components/Modals/ManageSettingModal";
import {MANAGE_NOTIFICATIONS} from "../../../Claims";
import useClaims from "../../../Hooks/useClaims";
import NotificationSettings from "../../../components/Notifications/NotificationSettings";

const SettingsContainer = ({appSettings, generalSettings, updateSettings, isUpdatingSettings, editable}) => {
    const {t} = useTranslation()
    const settingToDelete = useRef({
        key: null
    })

    const [settingToEdit, setSettingToEdit] = useState(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showAddModal, setShowAddModal] = useState(false)

    const selectedTab = useRef(SETTINGS_APP)
    const {hasClaim} = useClaims()
    // Higher order function to update settings for either app or general settings.
    function updSettings(settings)
    {
        updateSettings(settings, selectedTab.current)
    }

    function handleAddBtnPressed()
    {
        setSettingToEdit({
            category: selectedTab.current
        })
        setShowAddModal(true)
    }

    function handleDeleteBtnPressed(key)
    {
        settingToDelete.current = {
            key: key,
        }
        setShowDeleteModal(true)
    }

    function handleEditBtnPressed(key)
    {
        const setting = selectedTab.current === SETTINGS_APP ? appSettings.find(s => s.key === key) : generalSettings.find(s => s.key === key)

        setSettingToEdit({...setting, category: selectedTab.current})
        setShowAddModal(true)
    }

    function addSetting(setting)
    {
        delete setting.category // Remove category from setting object
        if(selectedTab.current === SETTINGS_APP)
        {
            const oldSettings = settingToEdit?.key != null ? appSettings.filter(s => s.key !== settingToEdit.key) : appSettings
            const settings = [...oldSettings, setting]

            updateSettings(settings, SETTINGS_APP)
        }
        else
        {
            const oldSettings = settingToEdit?.key != null ? generalSettings.filter(s => s.key !== settingToEdit.key) : generalSettings
            const settings = [...oldSettings, setting]

            updateSettings(settings, SETTINGS_GENERAL)
        }

        setShowAddModal(false)
        setSettingToEdit({
            key: null,
            category: selectedTab?.current ?? SETTINGS_APP,
            type: "String",
            value: null,
            displayName: null,
            description: null
        })
    }

    function deleteSetting()
    {
        const settings = selectedTab.current === SETTINGS_APP ? appSettings.filter(s => s.key !== settingToDelete.current.key) : generalSettings.filter(s => s.key !== settingToDelete.current.key)

        updateSettings(settings, selectedTab.current)
        setShowDeleteModal(false)
    }

    return (
        <Container className='px-4 py-3'>
            <Row>
                <Col className='ps-0'>
                    <h2 className='text-center'>{t('pages.settings.title')}</h2>
                </Col>
            </Row>
            <Row className='mt-2'>
                <Col className={css.wrapper}>
                    <Tabs variant='underline'
                          defaultActiveKey={SETTINGS_APP}
                          className="mb-4 px-2"
                          onSelect={(tab) => selectedTab.current = tab}
                    >

                        <Tab eventKey={SETTINGS_APP} title={t('pages.settings.appSettings')}>
                            {appSettings ?
                                <SettingsComponent settings={appSettings}
                                                   onUpdate={updSettings}
                                                   isUpdatingSettings={isUpdatingSettings}
                                                   editable={editable}
                                                   onAdd={handleAddBtnPressed}
                                                   onEdit={handleEditBtnPressed}
                                                   onDelete={handleDeleteBtnPressed}/> : <LoadingSpinner small text={`${t('pages.settings.fetchingSettings')}...`}/>}
                        </Tab>
                        <Tab eventKey={SETTINGS_GENERAL} title={t('pages.settings.generalSettings')}>
                            {generalSettings ?
                                <SettingsComponent settings={generalSettings}
                                                   onUpdate={updSettings}
                                                   isUpdatingSettings={isUpdatingSettings}
                                                   editable={editable}
                                                   onAdd={handleAddBtnPressed}
                                                   onEdit={handleEditBtnPressed}
                                                   onDelete={handleDeleteBtnPressed}/> : <LoadingSpinner small text={`${t('pages.settings.fetchingSettings')}...`}/>}
                        </Tab>
                        {
                            hasClaim(MANAGE_NOTIFICATIONS) &&
                            <Tab title={t('pages.settings.notificationsTabTitle')} eventKey="notifications">
                                <NotificationSettings/>
                            </Tab>
                        }
                    </Tabs>
                </Col>
            </Row>

            <ConfirmModal
                active={showDeleteModal}
                onCancel={()=> setShowDeleteModal(false)}
                onConfirm={deleteSetting}
                title={t('dialogs.confirmDeleteDialog.title')}
                positiveButton= {{text: t('global.operations.delete'), variant: "danger"}}>
                <span>{t('dialogs.confirmDeleteDialog.youAreAboutTo')} <strong style={{color: 'red'}}>{t('dialogs.confirmDeleteDialog.delete')}</strong> setting <strong>{settingToDelete.current.key}</strong>. {t('dialogs.confirmDeleteDialog.areYouSure')}</span>
            </ConfirmModal>

            {/*Add Setting*/}
            <ManageSettingModal
                title={settingToEdit?.key ? "Edit Setting" : "Add Setting"}
                active={showAddModal}
                settingToEdit={settingToEdit}
                onCancel={()=> setShowAddModal(false)}
                onConfirm={addSetting}
            >

            </ManageSettingModal>
        </Container>
    );
};

export default SettingsContainer;